import { apiClient } from './client'
import type {
  StateLegislator,
  StateLegislatorDetail,
  StateLegislatureOverview,
  StateVoteSummary,
  StateBillSummary,
  Politician,
  PoliticianDetail,
  PoliticianListResponse,
  VoteListResponse,
  VotingSummary,
  VotingHistoryResponse,
  KeyVote,
  CommitteeAssignment,
  CampaignFinance,
  TopDonor,
  StatesMapResponse,
  DistrictInfo,
} from './types'

// Politicians
export async function getPoliticians(params?: {
  state?: string
  party?: string
  chamber?: string
  q?: string
  page?: number
  page_size?: number
}): Promise<PoliticianListResponse> {
  const { data } = await apiClient.get('/politicians', { params })
  return data
}

// Alias for backwards compatibility
export const listPoliticians = getPoliticians

export async function getPoliticiansByState(state: string): Promise<Politician[]> {
  const { data } = await apiClient.get(`/politicians/by-state/${state}`)
  return data
}

export async function getPolitician(id: string): Promise<PoliticianDetail> {
  const { data } = await apiClient.get(`/politicians/${id}`)
  return data
}

// Votes
export async function getPoliticianVotes(
  politicianId: string,
  params?: { page?: number; page_size?: number }
): Promise<VoteListResponse> {
  const { data } = await apiClient.get(`/votes/by-politician/${politicianId}`, { params })
  return data
}

export async function getVotingSummary(politicianId: string): Promise<VotingSummary> {
  const { data } = await apiClient.get(`/votes/summary/${politicianId}`)
  return data
}

export async function getVotingHistory(politicianId: string): Promise<VotingHistoryResponse> {
  const { data } = await apiClient.get(`/votes/history/${politicianId}`)
  return data
}

export async function getKeyVotes(
  politicianId: string,
  params?: { limit?: number }
): Promise<KeyVote[]> {
  const { data } = await apiClient.get(`/votes/key-votes/${politicianId}`, { params })
  return data
}

export async function getCommitteeAssignments(
  politicianId: string
): Promise<CommitteeAssignment[]> {
  const { data } = await apiClient.get(`/politicians/${politicianId}/committees`)
  return data
}

// Finance
export async function getPoliticianFinance(politicianId: string): Promise<{
  items: CampaignFinance[]
  total_raised_all_cycles: number | null
  total_spent_all_cycles: number | null
}> {
  const { data } = await apiClient.get(`/finance/by-politician/${politicianId}`)
  return data
}

export async function getTopDonors(
  politicianId: string,
  params?: { cycle?: number; limit?: number }
): Promise<{ items: TopDonor[]; cycle: number; total: number }> {
  const { data } = await apiClient.get(`/finance/donors/${politicianId}`, { params })
  return data
}

// Map Data
export async function getStatesAggregation(): Promise<StatesMapResponse> {
  const { data } = await apiClient.get('/map/states')
  return data
}

export async function getStateDistricts(state: string): Promise<{
  state: string
  districts: DistrictInfo[]
  senators: Array<{
    id: string
    name: string
    party: string | null
    transparency_score: number | null
  }>
}> {
  const { data } = await apiClient.get(`/map/districts/${state}`)
  return data
}

export async function getPartyBreakdown(): Promise<{
  house: { D: number; R: number; I: number }
  senate: { D: number; R: number; I: number }
  total: { D: number; R: number; I: number }
}> {
  const { data } = await apiClient.get('/map/party-breakdown')
  return data
}

// State Legislature
export async function getStateLegislators(state: string, params?: {
  chamber?: 'upper' | 'lower'
  party?: string
}): Promise<StateLegislator[]> {
  const { data } = await apiClient.get(`/state-legislature/states/${state}/legislators`, { params })
  return data
}

export async function getStateLegislatureOverview(state: string): Promise<StateLegislatureOverview> {
  const { data } = await apiClient.get(`/state-legislature/states/${state}/overview`)
  return data
}

export async function getStateBills(state: string, params?: {
  session?: string
  limit?: number
  offset?: number
}): Promise<StateBillSummary[]> {
  const { data } = await apiClient.get(`/state-legislature/states/${state}/bills`, { params })
  return data
}

export interface StateCommittee {
  id: string
  name: string
  chamber: string
  classification: string
  member_count: number
}

export async function getStateCommittees(state: string): Promise<StateCommittee[]> {
  const { data } = await apiClient.get(`/state-legislature/states/${state}/committees`)
  return data
}

// State Legislator Detail
export async function getStateLegislator(id: string): Promise<StateLegislatorDetail> {
  const { data } = await apiClient.get(`/state-legislature/legislators/${id}`)
  return data
}

export async function getStateLegislatorVotes(id: string, params?: {
  limit?: number
  offset?: number
}): Promise<StateVoteSummary[]> {
  const { data } = await apiClient.get(`/state-legislature/legislators/${id}/votes`, { params })
  return data
}

export async function getStateLegislatorBills(id: string, params?: {
  limit?: number
  offset?: number
}): Promise<StateBillSummary[]> {
  const { data } = await apiClient.get(`/state-legislature/legislators/${id}/bills`, { params })
  return data
}

export async function getStateLegislatorCommittees(id: string): Promise<Array<{
  id: string
  name: string
  chamber: string
  classification: string
  role: string
}>> {
  const { data } = await apiClient.get(`/state-legislature/legislators/${id}/committees`)
  return data
}

export async function getStateLegislatorVotingSummary(id: string): Promise<{
  total_votes: number
  yes_votes: number
  no_votes: number
  abstain: number
  other: number
  participation_rate: number
}> {
  const { data } = await apiClient.get(`/state-legislature/legislators/${id}/voting-summary`)
  return data
}

export async function getStateLegislatorTransparencyScore(id: string): Promise<{
  score: number
  breakdown: {
    vote_participation: number
    bills_sponsored: number
    bills_passed: number
    public_info: number
  }
  max_scores: {
    vote_participation: number
    bills_sponsored: number
    bills_passed: number
    public_info: number
  }
}> {
  const { data } = await apiClient.get(`/state-legislature/legislators/${id}/transparency-score`)
  return data
}

export async function getStateLegislatorActivity(id: string, limit?: number): Promise<Array<{
  type: 'vote' | 'bill'
  date: string
  title: string
  description: string
  detail: string
  passed: boolean | null
}>> {
  const { data } = await apiClient.get(`/state-legislature/legislators/${id}/activity`, {
    params: { limit }
  })
  return data
}

// State Bill Detail
export async function getStateBillDetail(id: string): Promise<{
  id: string
  openstates_id: string
  state: string
  session: string
  identifier: string
  title: string
  abstract: string | null
  summary_ai: string | null
  classification: string[] | null
  subjects: string[] | null
  sponsor_id: string | null
  sponsor_name: string | null
  latest_action: string | null
  latest_action_date: string | null
  introduced_date: string | null
  openstates_url: string | null
  state_url: string | null
}> {
  const { data } = await apiClient.get(`/state-legislature/bills/${id}`)
  return data
}

export async function getStateBillVotes(id: string): Promise<Array<{
  date: string
  motion: string
  chamber: string
  passed: boolean | null
  yes_count: number | null
  no_count: number | null
  absent_count: number | null
  votes: Array<{
    legislator_id: string
    legislator_name: string
    party: string | null
    position: string
  }>
}>> {
  const { data } = await apiClient.get(`/state-legislature/bills/${id}/votes`)
  return data
}
